import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBook, 
  FaArrowLeft, 
  FaExternalLinkAlt, 
  FaGlobe, 
  FaChild, 
  FaBalanceScale, 
  FaUniversity, 
  FaShieldAlt, 
  FaCogs 
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import style from './AIPolicyAppendix5.module.css';

const AIPolicyAppendix5 = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0 }
  };

  const categories = [
    {
      id: 'national',
      title: 'Ulusal Politika ve Etik Çerçeveler',
      icon: <FaUniversity />,
      links: [
        { name: 'T.C. Millî Eğitim Bakanlığı – Yapay Zekâ Etik İlkeleri ve Rehberleri', url: 'https://www.meb.gov.tr' },
        { name: 'T.C. Millî Eğitim Bakanlığı – Türkiye Yüzyılı Maarif Modeli', url: 'https://tymm.meb.gov.tr' },
        { name: 'T.C. Cumhurbaşkanlığı Dijital Dönüşüm Ofisi – Ulusal Yapay Zekâ Stratejisi (2021–2025)', url: 'https://cbddo.gov.tr/uyzs' }
      ]
    },
    {
      id: 'children',
      title: 'Çocuk ve Öğrenci Güvenliği',
      icon: <FaChild />,
      links: [
        { name: 'UNICEF – Policy Guidance on AI for Children', url: 'https://www.unicef.org/globalinsight/reports/policy-guidance-ai-children' }
      ]
    },
    {
      id: 'legal',
      title: 'Hukuki ve Düzenleyici Çerçeveler',
      icon: <FaBalanceScale />,
      links: [
        { name: '6698 Sayılı Kişisel Verilerin Korunması Kanunu (KVKK)', url: 'https://www.mevzuat.gov.tr/mevzuat?MevzuatNo=6698&MevzuatTur=1&MevzuatTertip=5' },
        { name: 'Kişisel Verileri Koruma Kurumu – Rehberler ve İlke Kararları', url: 'https://www.kvkk.gov.tr' },
        { name: 'General Data Protection Regulation (GDPR)', url: 'https://eur-lex.europa.eu/eli/reg/2016/679/oj' },
        { name: 'European Union – Artificial Intelligence Act (EU AI Act)', url: 'https://artificialintelligenceact.eu' }
      ]
    },
    {
      id: 'ethics',
      title: 'Etik ve Uluslararası Meşruiyet Çerçeveleri',
      icon: <FaGlobe />,
      links: [
        { name: 'UNESCO – Recommendation on the Ethics of Artificial Intelligence', url: 'https://www.unesco.org/en/artificial-intelligence/recommendation-ethics' },
        { name: 'OECD – Principles on Artificial Intelligence', url: 'https://oecd.ai/en/ai-principles' }
      ]
    },
    {
      id: 'standards',
      title: 'Yönetişim, Risk ve Standartlar',
      icon: <FaCogs />,
      links: [
        { name: 'ISO/IEC 42001 – Artificial Intelligence Management System (AIMS)', url: 'https://www.iso.org/standard/81230.html' },
        { name: 'ISO/IEC 23894 – Artificial Intelligence Risk Management', url: 'https://www.iso.org/standard/77304.html' },
        { name: 'NIST – AI Risk Management Framework (AI RMF 1.0)', url: 'https://www.nist.gov/itl/ai-risk-management-framework' }
      ]
    },
    {
      id: 'security',
      title: 'Bilgi Güvenliği ve Mahremiyet Standartları',
      icon: <FaShieldAlt />,
      links: [
        { name: 'ISO/IEC 27001 – Information Security Management Systems', url: 'https://www.iso.org/isoiec-27001-information-security.html' },
        { name: 'ISO/IEC 27701 – Privacy Information Management Systems (PIMS)', url: 'https://www.iso.org/standard/71670.html' }
      ]
    }
  ];

  return (
    <div className={style.pageContainer}>
      <header className={style.header}>
        <div className={style.headerContent}>
          <Link to="/ai-policy" className={style.backLink}>
            <FaArrowLeft /> Politika Belgesine Dön
          </Link>
          <div className={style.badge}>
            <FaBook className={style.badgeIcon} />
            <span>EK–5: REFERANS DOKÜMANLAR</span>
          </div>
          <h1 className={style.title}>Politika Kaynakçası ve <br/> Referans Dokümanlar</h1>
          <p className={style.subtitle}>
            Talent14 Yapay Zekâ Politika Belgesi kapsamında dayanılan tüm resmî, kamusal ve doğrulanabilir kaynaklar.
          </p>
        </div>
      </header>

      <main className={style.mainContent}>
        <section className={style.introSection}>
          <p>Aşağıda yer alan referans dokümanlar, Talent14 Yapay Zekâ Politika Belgesi kapsamında kullanılan tüm dayanılan çerçevelerin resmî, kamusal ve doğrulanabilir kaynaklarını içermektedir.</p>
        </section>

        <motion.div 
          className={style.categoryGrid}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {categories.map((cat) => (
            <motion.div key={cat.id} variants={itemVariants} className={style.categoryCard}>
              <div className={style.catHeader}>
                <div className={style.catIcon}>{cat.icon}</div>
                <h2>{cat.title}</h2>
              </div>
              <div className={style.linkList}>
                {cat.links.map((link, idx) => (
                  <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className={style.resourceLink}>
                    <div className={style.linkContent}>
                      <span className={style.linkName}>{link.name}</span>
                      <span className={style.linkUrl}>{link.url}</span>
                    </div>
                    <FaExternalLinkAlt className={style.extIcon} />
                  </a>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </main>

      <footer className={style.pageFooter}>
        <p>© {new Date().getFullYear()} Talent14 - Doğrulanabilir ve Şeffaf Politika Kaynakları</p>
      </footer>
    </div>
  );
};

export default AIPolicyAppendix5;
